<script>
import partnerChoices from '@/mixins/partnerChoices';
import {hasEditPermission,currentRoleLevelName,trialActiveForClients} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';

export default {
	mixins: [partnerChoices],
	props: ['agreementData','hideForEdit','itemIndexToShow','forceAddNewItem'],
	data() {
		return {
			isViewMode : !hasEditPermission('section-property'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		};
	},
	methods: {
		newBusiness() {
			return {
				address: {},
				exclusions: [],
				additionalClauses: [],
				additionalClausesToRecitals: []
			};
		},
	},
	computed:{
		...mapState(['currentRoleLevel']),
		clientUserAgreement(){
			return trialActiveForClients() || currentRoleLevelName === 'client-sharing-user';
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
	},
	mounted(){
		for (let i = 0; i < this.agreementData.businesses.length; i++) {
			const business = this.agreementData.businesses[i];
			if (!business.hasOwnProperty('additionalClausesToRecitals')) {
				this.$set(business, 'additionalClausesToRecitals', []);
			}
		}	
	}
};
</script>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Businesses</h4>
			<a href="#" onclick="Intercom('showArticle', 11106840)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<list-item
			v-slot="{ item: business, index: index }"
			v-model="agreementData.businesses"
			:object-prototype="newBusiness"
			:testid="'business-name-'"
			:hideAddBtn=hideForEdit
			:isEditView=hideForEdit
			:itemIndexToShow=itemIndexToShow
			:forceAddNewItem=forceAddNewItem
			:disabled="isViewMode"
		>
		<template v-if="!forceAddNewItem || (forceAddNewItem && index == (agreementData.businesses.length-1))">
			<input-group
				v-model="business.name"
				reset-undefined
				class="mb-2"
				input-cols="8"
				label="Name"
				:testid="'business-name-'+index"
				:disabled="isViewMode"
			/>

			<address-input-group v-model="business.address" class="my-3" :testid="'business-address-'+index" :disabled="isViewMode"/>

			<radio-group
				v-model="business.ownership"
				class="my-3"
				:agreement-data="agreementData"
				input-cols="4"
				label="Ownership"
				reset-undefined
				partner
				select
				:options="['Jointly']"
				:testid="'business-ownership-'+index"
				:disabled="isViewMode"
			/>

			<input-group
				v-model="business.valuation"
				reset-undefined
				class="my-3"
				dollar
				input-cols="4"
				label="What is the value of the business?"
				:testid="'business-valuation-'+index"
				:disabled="isViewMode"
				:toolTipText="`Under the law, the value should be as of the date of the
				Agreement unless otherwise agreed. This can be tricky to determine depending on
				the type of business you have. You may wish to talk to your accountant for
				help.`"
			/>
			<template v-if="!clientUserAgreement">
				<yes-no-choice
					v-model="business.professionalValuation"
					reset-undefined
					label="Did this value come from a professional business valuation?"
					class="my-2"
					:testid="'business-professional-valuation-'+index"
					:disabled="isViewMode"
				/>

				<conditional-group
					class="my-3"
					:condition="business.professionalValuation"
				>
					<date-input
						v-model="business.valuationDate"
						reset-undefined
						label="Date of valuation"
						:testid="'business-valuation-date-'+index"
						:disabled="isViewMode"
					/>
				</conditional-group>

				<yes-no-choice
					v-model="business.hasExclusions"
					reset-undefined
					label="Are there any exclusions related to the business?"
					class="my-3"
					:testid="'business-has-exclusions-'+index"
					:disabled="isViewMode"
					:toolTipText="`An exclusion under the law is a portion of the value that should
					be retained by the person rather than shared. Legal exclusions include parts
					that were obtained through inheritance, gift, or property that was owned before
					you and your spouse began living together. The amount claimed should be the
					original amount received or existing when you started to live together. All
					growth in the value of the property will be shared under the law. Learn more in
					the Knowledge Base in this section. There is an option to agree to exclude a
					portion of the property.`"
				/>

				<conditional-group class="my-3" :condition="business.hasExclusions">
					<exclusion-table
						v-model="business.exclusions"
						:agreement-data="agreementData"
						:testid="'business-exclusion-'+index+'-'"
						:disabled="isViewMode"
					/>
				</conditional-group>

				<radio-group
					v-model="business.plan"
					class="my-2"
					:agreement-data="agreementData"
					input-cols="4"
					label="What will the owner do with the business upon separation?"
					reset-undefined
					select
					:options="['Keep', 'Transfer', 'Sell']"
					:testid="'business-upon-seperation-'+index"
					:disabled="isViewMode"
					:toolTipText="`Practically who will continue to run the business? `"
				/>

				<conditional-group
					class="my-3"
					:condition="
						business.plan === 'Transfer' &&
						business.ownership === 'Jointly'
					"
				>
					<radio-group
						v-model="business.transferor"
						class="mb-2"
						:agreement-data="agreementData"
						input-cols="4"
						:label="`Given that ${isUserSeparatingParty? 'you':'the parties'} jointly own the business, who is transferring their ownership?`"
						reset-undefined
						partner
						select
						:testid="'business-transferor-'+index"
						:disabled="isViewMode"
					/>
				</conditional-group>

				<additional-clauses class="mt-2" :newLabel="'Add Clause to Recitals'" :title="'Additional Clauses into Recitals'" v-model="business.additionalClausesToRecitals" :testid="'business-add-clause-to-recitals-'+index+'-'"
				:disabled="isViewMode"/>
				<additional-clauses class="mt-2" :newLabel="'Add Clause to Section'" :title="'Additional Clauses into Sections'" v-model="business.additionalClauses" :testid="'business-add-clause-'+index+'-'"
				:disabled="isViewMode"/>
			</template>
		</template>
		</list-item>
	</b-card>
</template>
